import React, { useState, useRef } from 'react';
import toast from 'react-hot-toast';
import { useVirtualizer } from '@tanstack/react-virtual';
import { FileText, Loader2, MapPin, MessageCircle, Search, RefreshCw } from 'lucide-react';
import ResultsFilters from './ResultsFilters';
import api from '../api';
import Modal from './Modal';

const maskPhone = (phone) => {
  if (!phone || phone === 'N/A') return '';
  const clean = String(phone).trim();
  if (clean.length < 5) return clean;
  return clean.slice(0, 6) + '*'.repeat(Math.min(clean.length - 6, 5));
};

const maskEmail = (emailStr) => {
  if (!emailStr || emailStr === 'N/A') return '';
  const emails = Array.isArray(emailStr) ? emailStr : String(emailStr).split(',').map(e => e.trim());
  return emails.map(email => {
    const parts = email.split('@');
    if (parts.length !== 2) return email.slice(0, 4) + '****';
    const name = parts[0];
    const domainParts = parts[1].split('.');
    const nameMasked = name.length > 3 ? name.slice(0, 3) + '*'.repeat(name.length - 3) : name.slice(0, 1) + '**';
    const domainMasked = domainParts.length > 1 ? '*'.repeat(domainParts[0].length) + '.' + domainParts.slice(1).join('.') : '****';
    return `${nameMasked}@${domainMasked}`;
  }).join(', ');
};

export default function AdminLeadsTable({
  results, filteredResults, status, resultsLoading,
  resultSearchInput, setResultSearchInput,
  categoryFilter, setCategoryFilter,
  sourceFilter, setSourceFilter,
  resultStateFilter, setResultStateFilter,
  contactFilter, setContactFilter,
  availableCategories, availableSources, availableResultStates,
  onClearResults,
}) {
  const [uploading, setUploading] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);

  const handleUploadMaster = async () => {
    setUploading(true);
    try {
      const res = await api.post('/upload-master');
      if (res.data?.success) {
        toast.success(`Sincronização concluída! ${res.data.total_rows} leads enviados.`);
      } else {
        toast.error(`Erro: ${res.data?.message || 'Falha na sincronização'}`);
      }
    } catch (e) {
      toast.error('Erro ao sincronizar: ' + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
    }
  };

  const parentRef = useRef(null);
  const reversedResults = [...filteredResults].reverse();
  const rowVirtualizer = useVirtualizer({
    count: reversedResults.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 160,
    overscan: 5,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  const paddingTop = virtualItems.length > 0 ? virtualItems[0]?.start || 0 : 0;
  const paddingBottom = virtualItems.length > 0
    ? rowVirtualizer.getTotalSize() - (virtualItems[virtualItems.length - 1]?.end || 0)
    : 0;

  return (
    <main id="base-de-leads" className="glass card" style={{ display: 'flex', flexDirection: 'column', padding: '0' }}>
      <div style={{ padding: '2rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '1.4rem' }}>
          <FileText size={24} color="var(--accent)" /> Banco de Leads
        </h3>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button
            onClick={handleUploadMaster}
            disabled={uploading}
            style={{ padding: '8px 15px', fontSize: '0.75rem', fontWeight: 700, background: 'var(--accent)', border: 'none', color: '#000', borderRadius: '20px', cursor: uploading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <RefreshCw size={14} className={uploading ? 'spin' : ''} />
            {uploading ? 'SINCRONIZANDO...' : 'SINCRONIZAR FIREBASE'}
          </button>
          <button
            onClick={onClearResults}
            style={{ padding: '8px 15px', fontSize: '0.75rem', fontWeight: 700, background: 'rgba(231, 76, 60, 0.1)', border: '1px solid rgba(231, 76, 60, 0.3)', color: '#e74c3c', borderRadius: '20px', cursor: 'pointer' }}
          >
            LIMPAR TODOS
          </button>
          <div className="glass" style={{ padding: '8px 20px', borderRadius: '30px', color: 'var(--accent)', fontWeight: 700, border: '1px solid var(--accent)' }}>
            {filteredResults.length} / {results.length} VISÍVEIS
          </div>
        </div>
      </div>

      <ResultsFilters
        resultSearchInput={resultSearchInput}
        setResultSearchInput={setResultSearchInput}
        categoryFilter={categoryFilter}
        setCategoryFilter={setCategoryFilter}
        sourceFilter={sourceFilter}
        setSourceFilter={setSourceFilter}
        resultStateFilter={resultStateFilter}
        setResultStateFilter={setResultStateFilter}
        contactFilter={contactFilter}
        setContactFilter={setContactFilter}
        availableCategories={availableCategories}
        availableSources={availableSources}
        availableResultStates={availableResultStates}
      />

      <div style={{ flex: 1, padding: '1rem' }}>
        <div ref={parentRef} style={{ maxHeight: '600px', overflowY: 'auto', borderRadius: 18, background: 'var(--bg-card-strong)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)' }}>
          {resultsLoading ? (
            <div style={{ padding: '6rem 2rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', opacity: 0.6 }}>
              <Loader2 size={36} className="spin" style={{ color: 'var(--accent)' }} />
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Carregando base de leads…</p>
            </div>
          ) : filteredResults.length === 0 ? (
            <div style={{ padding: '12rem 2rem', textAlign: 'center', opacity: 0.2 }}>
              <Search size={80} style={{ marginBottom: '1.5rem' }} />
              <p style={{ fontSize: '1.2rem' }}>
                {results.length === 0 ? 'O banco de dados está vazio no momento.' : 'Nenhum lead atende aos filtros atuais.'}
              </p>
            </div>
          ) : (
            <table className="results-table">
              <thead>
                <tr>
                  <th>Empresa</th>
                  <th>Estado</th>
                  <th>Contatos Diretos</th>
                  <th>E-mails Encontrados</th>
                  <th style={{ width: '120px' }}>Fonte</th>
                </tr>
              </thead>
              <tbody>
                {paddingTop > 0 && (
                  <tr>
                    <td style={{ height: `${paddingTop}px`, padding: 0, border: 0 }} colSpan={5} />
                  </tr>
                )}
                {virtualItems.map((virtualRow) => {
                  const r = reversedResults[virtualRow.index];
                  const i = virtualRow.index;
                  return (
                  <tr key={i} className="animate-fade-in" style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer', transition: 'background 0.2s' }} onClick={() => setSelectedLead(r)} onMouseEnter={(e) => e.currentTarget.style.background = 'var(--table-row-hover)'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                    <td>
                      <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-primary)' }}>{r.Nome}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '5px', marginTop: '4px' }}>
                        <MapPin size={12} /> {r.Endereço}
                      </div>
                      <div className="result-meta-line">Busca: {r.Busca || r.Categoria}</div>
                      {r.SequenciaBusca ? <div className="result-meta-line">Sequência: {r.SequenciaBusca}</div> : null}
                      <div className="tag-pills">
                        {(Array.isArray(r.Tags) ? r.Tags : []).slice(0, 5).map((tag, ti) => (
                          <span key={`${r.Nome || 'lead'}-tag-${ti}-${tag}`} className="tag-pill">{tag}</span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontSize: '0.9rem', color: 'var(--accent)', fontWeight: 600 }}>{r.Estado || 'N/A'}</div>
                      <div className="result-meta-line">{r.Categoria}</div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {r.WhatsApp || (r.Telefone && r.Telefone !== 'N/A') ? (
                          <div style={{ color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', fontWeight: 700 }}>
                            <MessageCircle size={16} fill="var(--accent)" style={{ color: 'var(--bg-main)' }} /> {maskPhone(r.WhatsApp || r.Telefone)}
                          </div>
                        ) : (
                          <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', opacity: 0.7 }}>Nenhum contato público</div>
                        )}
                        <div className="tag-pills">
                          {(Array.isArray(r.Canais) ? r.Canais : []).map((channel, ci) => (
                            <span key={`${r.Nome || 'lead'}-ch-${ci}`} className="tag-pill tag-pill--channel">{channel}</span>
                          ))}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {r.Emails && r.Emails !== 'N/A' ? (
                          <span style={{ padding: '3px 10px', borderRadius: '4px', color: '#f1c40f', fontSize: '0.9rem', border: '1px solid rgba(241, 196, 15, 0.2)', wordBreak: 'break-all' }}>
                            {maskEmail(r.Emails)}
                          </span>
                        ) : (
                          <span style={{ opacity: 0.3 }}>Nenhum e-mail detectado</span>
                        )}
                      </div>
                      <div className="tag-pills">
                        {(Array.isArray(r.PalavrasChave) ? r.PalavrasChave : []).slice(0, 5).map((kw, ki) => (
                          <span key={`${r.Nome || 'lead'}-kw-${ki}`} className="tag-pill tag-pill--keyword">{kw}</span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <div style={{
                        fontSize: '0.7rem', padding: '4px 12px', borderRadius: '20px', textAlign: 'center',
                        background: r.Fonte === 'Google API' ? 'rgba(52, 152, 219, 0.1)' : 'rgba(155, 89, 182, 0.1)',
                        color: r.Fonte === 'Google API' ? '#3498db' : '#9b59b6',
                        border: `1px solid ${r.Fonte === 'Google API' ? 'rgba(52, 152, 219, 0.2)' : 'rgba(155, 89, 182, 0.2)'}`,
                        fontWeight: 800, textTransform: 'uppercase',
                      }}>
                        {r.Fonte}
                      </div>
                    </td>
                  </tr>
                  );
                })}
                {paddingBottom > 0 && (
                  <tr>
                    <td style={{ height: `${paddingBottom}px`, padding: 0, border: 0 }} colSpan={5} />
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Modal open={!!selectedLead} onClose={() => setSelectedLead(null)}>
        {selectedLead && (
          <div style={{ color: 'var(--text-primary)' }}>
            <h2 style={{ margin: '0 0 1.5rem 0', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '1.4rem' }}>
              <MessageCircle size={24} color="var(--accent)" /> Detalhes do Contato
            </h2>
            <div style={{ background: 'var(--glass-element-bg)', padding: '16px', borderRadius: '12px', border: '1px solid var(--border)', marginBottom: '16px' }}>
              <h4 style={{ margin: '0 0 6px 0', opacity: 0.8, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Telefone / WhatsApp</h4>
              <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--accent)', fontFamily: 'var(--font-mono)' }}>
                {maskPhone(selectedLead.WhatsApp || selectedLead.Telefone) || 'N/A'}
              </div>
            </div>
            <div style={{ background: 'var(--glass-element-bg)', padding: '16px', borderRadius: '12px', border: '1px solid var(--border)', marginBottom: '16px' }}>
              <h4 style={{ margin: '0 0 6px 0', opacity: 0.8, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>E-mail Principal</h4>
              <div style={{ fontSize: '1.15rem', fontWeight: 'bold', wordBreak: 'break-all' }}>
                {maskEmail(selectedLead.Emails) || 'N/A'}
              </div>
            </div>
            <div style={{ background: 'var(--glass-element-bg)', padding: '16px', borderRadius: '12px', border: '1px solid var(--border)' }}>
              <h4 style={{ margin: '0 0 6px 0', opacity: 0.8, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Segmento / Categoria</h4>
              <div style={{ fontSize: '1.15rem', fontWeight: 'bold' }}>
                {selectedLead.Categoria || 'Não informado'}
              </div>
            </div>
            <div style={{ marginTop: '1.5rem', textAlign: 'center', opacity: 0.5, fontSize: '0.8rem' }}>
              Os dados completos estarão disponíveis após pagamento e liberação.
            </div>
          </div>
        )}
      </Modal>
    </main>
  );
}
